import React, {useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {emitPaddleEvent, initializePaddle} from '../lib/paddle';
import {fetchSubscriptions} from '../store/slices/subscriptionThunks';
import {isAuthenticatedSelector} from '../store/selectors/auth.selectors';
import {debugLog} from '../lib/debug';

// Paddle reports `checkout.completed` the moment the overlay closes, slightly before the
// subscription is queryable. The marketing site waited 2s via a sessionStorage flag read on the
// next page load; we just wait here and refetch through the thunk.
const SUBSCRIPTION_SETTLE_MS = 2000;

/**
 * Loads and initialises Paddle.js once, and owns the global checkout event handling.
 * Renders nothing.
 *
 * Paddle takes exactly one `eventCallback`, kept for the lifetime of the page, so this is also where
 * every other checkout event listener gets fed — see `emitPaddleEvent`. The callback deliberately has
 * no "has this component unmounted" guard: in StrictMode the effect runs twice, the second
 * `initializePaddle` is a no-op on the memoised promise, and a callback that opted out during the
 * first cleanup would silently take every checkout event down with it.
 */
const PaddleInitializer: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);

  // Paddle initialises once per page load, so its callback would otherwise close over whatever
  // auth state happened to be current at mount — which is "signed out" on a cold load.
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  useEffect(() => {
    initializePaddle((event) => {
      debugLog('[Paddle] Checkout event:', event.name);
      emitPaddleEvent(event);

      // Everything below is the *signed-in* upgrade checkout. During signup there is no session yet
      // and the signup screen owns what happens next: navigating to /account would bounce a visitor
      // who has just paid onto the login page, and navigating home would abandon the flow.
      if (!isAuthenticatedRef.current) {
        return;
      }

      if (event.name === 'checkout.completed') {
        // Refresh billing state, then land the user on their account page.
        setTimeout(() => dispatch(fetchSubscriptions()), SUBSCRIPTION_SETTLE_MS);
        navigate('/account');
        return;
      }

      if (event.name === 'checkout.closed') {
        navigate('/account');
      }
    });
    // `dispatch` and `navigate` are stable for the lifetime of the app; auth is read via the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default PaddleInitializer;
